import type { LabDemosLocale } from '../types'

export const zhLabDemos: LabDemosLocale = {
  eventDriven: {
    pendingLabel: '待处理事件',
    pendingHint: '一条真实的告警正在队列里。点 fire,看 ontology 现场合成响应。',
    fireButton: '▶ 发射事件',
    refireButton: '↻ 再发一次',
    resetButton: '重置',
    goalSelectorLabel: '当前目标',
    goalSelectorHint: '同一条告警 + 不同目标 → 完全不同的响应路径。',
    emptyStateTitle: '等待事件',
    emptyStateBody:
      '点左侧 fire 按钮。Ontology 会读取事件、推理受影响实体、评估约束、当场提出动作 — 全部现场合成。',
    activeEventLabel: '活动事件',
    insightTitle: '刚刚发生了什么',
    insightBody:
      '右边的路径是 fire 时刻当场合成的 — 不是从预存的 SOP 里取出来的。换一个目标,同一条告警就会得到不同的链。',
    event: {
      code: '报警 K-3041',
      meta: '02:14 · CGS-3 · P-1023A',
      summary: '高含硫进料泵 · H₂S 浓度突增',
      descriptor: 'H₂S 12.6% → 18.2%,47 秒内 +44%',
      payloadLabel: '负载',
      triggerNodeLabel: 'Event::ALARM_K-3041',
      triggerNodeDetail: '来源 PI 标签 CGS3.P1023A.H2S · 02:14:07 接收',
    },
    goals: [
      { id: 'compliance', label: '合规优先' },
      { id: 'uptime', label: '不停产' },
      { id: 'cost', label: '最低总成本' },
    ],
  },
  goalDriven: {
    wishesLabel: '挑一个 wish',
    wishesHint:
      '每个 wish 就是一句人话目标。选中后,ontology 会反向规划出满足所有硬约束的执行路径。',
    insightTitle: '同一本体,不同计划',
    insightBody:
      '下面的图没有任何预存。每次选 wish 都是用不同的意图询问同一个 ontology — 当场推出一条执行链。',
    wishes: [
      {
        id: 'wish-compliance',
        goalId: 'compliance',
        quote: '"给 P-1023 找一个 2 周交期的合规机封。"',
        speaker: '采购 · M. Chen',
      },
      {
        id: 'wish-uptime',
        goalId: 'uptime',
        quote: '"在替换设备到位之前,保 P-1023A 不停 — 给我一个过渡方案。"',
        speaker: '生产 · 现场工程师',
      },
      {
        id: 'wish-cost',
        goalId: 'cost',
        quote: '"本季度总成本最低 — 含停产损失和事故概率。"',
        speaker: '财务 · CFO 办公室',
      },
    ],
  },
  explainability: {
    decisionsLabel: '历史决策',
    decisionsHint:
      '挑一条审计可能会问的问题。Ontology 会把这个决策展开成完整的因果链。',
    emptyStateTitle: '尚未选择决策',
    emptyStateBody: '点左边的任一问题 — 所有贡献节点亮起,其余淡出。',
    clearButton: '清除 ✕',
    chainTitle: '因果链',
    chainHint: '每一节都是 ontology 里独立的实体,生产环境可继续下钻。',
    insightTitle: '可审计是天生的',
    insightBody:
      'Ontology 推出的每一个动作都顺手带着这条链。多年以后还能重放 — 来源信息长在图上,不是事后日志补的。',
    decisions: [
      {
        id: 'decision-act-viol',
        question: '"为什么推荐 expandRfq + raiseMOC?"',
        goalId: 'compliance',
        nodeId: 'g1-act',
      },
      {
        id: 'decision-c-plan',
        question: '"为什么禁了 P-1023 的 Plan 53A?"',
        goalId: 'compliance',
        nodeId: 'g1-c-plan',
      },
      {
        id: 'decision-c-mat',
        question: '"为什么这个工况禁了 SS316?"',
        goalId: 'compliance',
        nodeId: 'g1-c-mat',
      },
      {
        id: 'decision-cand-viol',
        question: '"为什么 A / B / C 都没进短名单?"',
        goalId: 'compliance',
        nodeId: 'g1-cand',
      },
      {
        id: 'decision-uptime-act',
        question: '"为什么对备机安排强化巡检?"',
        goalId: 'uptime',
        nodeId: 'g2-act',
      },
    ],
  },
  documentDriven: {
    documentsLabel: '样板文档',
    documentsHint: '点任一份"上传"。看 LLM 抽实体、ontology 接住、现场合成处理链。',
    extractingLabel: '正在抽取实体…',
    extractedLabel: '实体已抽取 · 待绑定',
    resetButton: '↻ 换一份',
    insightTitle: '不同文档,不同链',
    insightBody:
      'LLM 负责非结构化文本的抽取,ontology 负责约束 + 落地,合成路径类型正确。同一种范式,因输入不同,路径完全不同。',
    documents: [
      {
        id: 'contract',
        icon: '📄',
        name: '采购合同-2025-Q2.pdf',
        meta: '14 页 · 采购部上传',
        summary: '与 Acme Pumps Inc. 的 3 年供货协议',
        entities: [
          { kind: '当事方', value: 'Acme Pumps Inc.' },
          { kind: '生效日', value: '2025-04-01' },
          { kind: '金额上限', value: '$840 万 / 年' },
          { kind: '条款', value: '自动续约 §12.3' },
          { kind: '风险', value: '不可抗力 §17 (宽泛)' },
        ],
      },
      {
        id: 'rfq',
        icon: '📋',
        name: 'RFQ-29117-机封.xlsx',
        meta: '3 张表 · 工程部上传',
        summary: 'P-1023 机封紧急采购询价',
        entities: [
          { kind: '设备', value: 'P-1023 (CGS-3)' },
          { kind: '投标方', value: 'A · B · C' },
          { kind: '强制证书', value: 'NACE MR0175 现行版' },
          { kind: '交期', value: '14 天 · 紧急 SLA' },
          { kind: '预算上限', value: '$30 万' },
        ],
      },
      {
        id: 'incident',
        icon: '⚠',
        name: '事故报告-K3041.md',
        meta: '6 节 · 生产部上传',
        summary: '高含硫泵 P-1023A 机封一级泄漏 · 凌晨',
        entities: [
          { kind: '资产', value: 'P-1023A' },
          { kind: '严重度', value: '一级泄漏 · 已控' },
          { kind: '发现时间', value: '02:14 当地' },
          { kind: '假设', value: '腐蚀 + H₂S 突增' },
          { kind: '影响范围', value: 'CGS-3 A 列' },
        ],
      },
    ],
  },
  questionGrounded: {
    questionsLabel: '试一个问题',
    questionsHint: '挑一条,看 ontology 沿图谱组装答案。点任一[方括号]里的实体可下钻。',
    emptyStateTitle: '尚未选问题',
    emptyStateBody:
      '左边挑一条。答案会引用它走过的每一个实体 — 可点击,一路追到数据源。',
    entityDetailLabel: '实体详情',
    insightTitle: '接地的回答,不是生成的回答',
    insightBody:
      '纯 RAG 给一段话加脚注。这里的答案是**沿图谱构造出来的** — 每一个论断都是一次实体跳转,每一条引用都是一个 node id,反驳意见会被记为候选约束修订。',
    questions: [
      {
        id: 'q-award-contract',
        question: '本周能给 Acme 签合同 X 吗?',
        answer:
          '合同对方是 [[entity:e1|Acme Pumps Inc.]]。根据 [[entity:e2|R-SIG-AUTH-01]],该合同金额($36 万)超过 [[entity:e3|$30 万阈值]],需要 VP 签字。Acme 在 [[entity:e4|集团 AVL 已审]],但 [[entity:e5|2024-11 起在油田级被暂停]],因此需配 [[entity:e6|MOC 豁免]] — 否则合同转法务走例外审批。',
        entities: [
          { id: 'e1', label: 'Acme Pumps Inc.', kind: 'object', detail: '4 年合作 · 12 份历史合同 · 默认风险评分 38/100' },
          { id: 'e2', label: 'R-SIG-AUTH-01', kind: 'constraint', detail: '≥ $30 万需 VP 签字;多年期合同还需告知董事会', citation: '集团 PA-2024-117 §3.1' },
          { id: 'e3', label: '$30 万阈值', kind: 'inference', detail: '从集团权限表按汇率换算 · 每季度复核' },
          { id: 'e4', label: 'AVL · 集团', kind: 'object', detail: '状态: 2021-06 起 APPROVED · 最近审计 2024-08' },
          { id: 'e5', label: 'AVL · 油田', kind: 'object', detail: '状态: 2024-11 SUSPENDED · 原因: 证书过期,复审中' },
          { id: 'e6', label: 'MOC 豁免', kind: 'action', detail: '7 天集团复核 · 附入合同档案 · 可审计追踪' },
        ],
      },
      {
        id: 'q-pump-running',
        question: '泵 P-1023A 还能跑多久?',
        answer:
          '根据 [[entity:e1|PI 实时数据]] 和 [[entity:e2|剩余寿命推理]],P-1023A 当前工况下还能撑 [[entity:e3|9-12 天]]。这低于 [[entity:e5|最快合规替换交期]] 的 14 天 — 触发 [[entity:e4|R-BACKUP-SLA-11]],强制 [[entity:e6|强化巡检 + 限运行]]。',
        entities: [
          { id: 'e1', label: 'PI 实时数据', kind: 'object', detail: '端面温度 92°C · 振动 3.2 mm/s · 30 天趋势上升' },
          { id: 'e2', label: '剩余寿命推理', kind: 'inference', detail: '腐蚀模型: H₂S 12.6%,腐蚀因子 1.32 · 基线 11 天 → 调整 8 天' },
          { id: 'e3', label: '9-12 天', kind: 'inference', detail: '腐蚀因子上下包络 · 每小时再算' },
          { id: 'e4', label: 'R-BACKUP-SLA-11', kind: 'constraint', detail: '备机寿命 < 最快合规交期 → 强制强化巡检 + 限运行', citation: '集团 EQ-BACKUP-SLA' },
          { id: 'e5', label: '最快合规替换交期', kind: 'inference', detail: '跨 AVL 计算 · D 供应商 · 14 天紧急交期' },
          { id: 'e6', label: '强化巡检 + 限运行', kind: 'action', detail: '每 4h 巡检 · 80% 负荷上限 · 日报至生产经理' },
        ],
      },
      {
        id: 'q-renewal-risk',
        question: '哪些客户续约有风险?',
        answer:
          '三个客户触发 [[entity:e1|R-RENEWAL-RISK-08]]: [[entity:e2|Theta 账户]](季度使用 -38%)、[[entity:e3|Lambda 账户]](3 个 ticket 超 SLA)、[[entity:e4|Sigma 账户]](决策者 2025-02 离职)。[[entity:e5|推荐触达方案]]: Theta 与 Lambda 7 天内由 CSM 接触;Sigma 转销售重做利益相关方映射。',
        entities: [
          { id: 'e1', label: 'R-RENEWAL-RISK-08', kind: 'constraint', detail: '复合规则: 使用下滑 + ticket 超期 + 关键人变动', citation: 'CS 手册 §4' },
          { id: 'e2', label: 'Theta 账户', kind: 'object', detail: 'MRR $4.5 万 · 续约 2025-Q3 · 90 天使用 -38%' },
          { id: 'e3', label: 'Lambda 账户', kind: 'object', detail: 'MRR $6.2 万 · 续约 2025-Q2 · 3 张 ticket > 14 天' },
          { id: 'e4', label: 'Sigma 账户', kind: 'object', detail: 'MRR $11.8 万 · 续约 2025-Q3 · 主负责人 2025-02-14 离职' },
          { id: 'e5', label: '推荐触达方案', kind: 'action', detail: '按客户分层的触达节奏 + 内容配方 + 会议节奏' },
        ],
      },
    ],
  },
  changePropagation: {
    controlLabel: 'NACE H₂S 阈值 (酸性服务)',
    controlHint:
      '拖动滑块设新阈值。Ontology 当场对每一台资产重评估,规则触发结果变了的资产状态徽章实时翻转。',
    thresholdLabel: '阈值',
    currentLabel: '当前',
    proposedLabel: '提议',
    unitLabel: 'ppm',
    resetButton: '恢复到当前',
    impactTitle: '全场影响',
    impactSummaryTpl: '{total} 台资产中 {affected} 台状态翻转',
    statusLabels: {
      standard: '标准',
      watch: '观察',
      sour: '酸性服务',
    },
    flipBadge: '已翻转',
    assetMetaLabel: 'H₂S 读数',
    insightTitle: '改一处规则,全图传播',
    insightBody:
      '阈值改一次,所有受影响资产的状态全图同步翻转,触发 MOC 复核,并标记所有引用旧阈值的在途 RFQ。换成 SOP 系统:每个引用过该规则的 Skill 都要单独改一遍 — 而审计链路从这里开始就是一个数字考古项目。',
    assets: [
      { id: 'a-p1023', code: 'P-1023', name: '进料泵 · CGS-3 A 列', h2sPpm: 12600 },
      { id: 'a-p1024', code: 'P-1024', name: '进料泵 · CGS-3 B 列', h2sPpm: 8500 },
      { id: 'a-v1010', code: 'V-1010', name: '酸气分液罐', h2sPpm: 3200 },
      { id: 'a-p1070', code: 'P-1070', name: '污水泵', h2sPpm: 1100 },
      { id: 'a-v1040', code: 'V-1040', name: '稳定塔进料罐', h2sPpm: 780 },
      { id: 'a-p1031', code: 'P-1031', name: '原油外输泵', h2sPpm: 420 },
      { id: 'a-p1050', code: 'P-1050', name: '甘醇循环泵', h2sPpm: 220 },
      { id: 'a-v1020', code: 'V-1020', name: '甜气洗涤器', h2sPpm: 180 },
      { id: 'a-v1060', code: 'V-1060', name: '洗水罐', h2sPpm: 80 },
      { id: 'a-p1032', code: 'P-1032', name: '柴油外输泵', h2sPpm: 35 },
    ],
    currentThresholdPpm: 500,
    thresholdRange: { min: 50, max: 2000, step: 10 },
  },
  riskScoring: {
    controlLabel: '为 Acme Pumps Inc. 评分',
    controlHint:
      '逐维度设值(0 = 最好 · 10 = 最差)。Ontology 把加权贡献合成一个供应商风险评分。',
    resetButton: '恢复基线',
    dimensionsTitle: '风险维度',
    dimensions: [
      { id: 'financial', label: '财务健康', weightPct: 40, description: '现金、杠杆、近期盈利趋势', defaultValue: 6 },
      { id: 'operational', label: '运营可靠性', weightPct: 35, description: '准时交付、缺陷率、质量投诉', defaultValue: 4 },
      { id: 'esg', label: 'ESG 与合规', weightPct: 25, description: '安全记录、制裁名单、审计发现', defaultValue: 5 },
    ],
    scoreTitle: '供应商风险评分',
    scoreCaption: '0 = 安全 · 100 = 严重',
    thresholds: {
      safe: { upper: 30, label: '可放心授标' },
      watch: { upper: 60, label: '观察 — 标记复核' },
      critical: { label: '严重 — 须先整改' },
    },
    attributionTitle: '评分如何拆分',
    attributionHint: '每个维度贡献 = 值 × 权重。任意拖一个 slider,评分和所有贡献项瞬时重算。',
    contributionFormula: '{value} × {weight}% = {contribution}',
    insightTitle: '评分自带解释',
    insightBody:
      '拖动任一 slider,一切实时重算。数字从不黑箱 — 每个贡献项都能点开,看背后的规则和数据源。',
    contextSupplier: {
      label: '正在评估的供应商',
      name: 'Acme Pumps Inc.',
      detail: '4 年合作 · 12 份历史合同 · 最近审计 2024-08',
    },
  },
  whatIf: {
    scenariosLabel: '假设情境',
    scenariosHint:
      '选一个未来轨迹。Ontology 按月正向推演 — 每一步对全部资产重新分类。注意哪些约束在何时被触发。',
    horizonLabel: '推演时长',
    monthsUnit: '个月',
    resetButton: '重置',
    legendLabel: '状态图例',
    monthAxisLabel: '月',
    insightTitle: '时间感知,不是瞬时',
    insightBody:
      'Ontology 在每个推演步骤都重评估整个资产组,不只是"现在"。这才能提前发现"下季度会触发"的约束,而不是事后补救。',
    scenarios: [
      {
        id: 'h2s-30',
        title: 'H₂S 6 个月内 +30%',
        description: '酸性服务温和漂移 — 每月 5%',
        h2sGrowthPctPerMonth: 5,
      },
      {
        id: 'h2s-80',
        title: '剧烈酸化 · 6 个月 +80%',
        description: '油藏变化推 H₂S 每月 13%',
        h2sGrowthPctPerMonth: 13,
      },
      {
        id: 'h2s-stable',
        title: '工况稳定',
        description: '0.5% 漂移 — 仅运行噪声,无实质变化',
        h2sGrowthPctPerMonth: 0.5,
      },
    ],
    horizonRange: { min: 3, max: 18, step: 1, default: 9 },
    baseThresholdPpm: 500,
    assets: [
      { id: 'a-p1023', code: 'P-1023', name: '进料泵 · CGS-3 A', h2sPpmStart: 12600 },
      { id: 'a-p1024', code: 'P-1024', name: '进料泵 · CGS-3 B', h2sPpmStart: 8500 },
      { id: 'a-v1010', code: 'V-1010', name: '酸气分液罐', h2sPpmStart: 3200 },
      { id: 'a-p1070', code: 'P-1070', name: '污水泵', h2sPpmStart: 600 },
      { id: 'a-v1040', code: 'V-1040', name: '稳定塔进料罐', h2sPpmStart: 380 },
      { id: 'a-p1031', code: 'P-1031', name: '原油外输泵', h2sPpmStart: 220 },
      { id: 'a-p1050', code: 'P-1050', name: '甘醇循环泵', h2sPpmStart: 160 },
      { id: 'a-v1020', code: 'V-1020', name: '甜气洗涤器', h2sPpmStart: 90 },
    ],
    statusLabels: {
      standard: '标准',
      watch: '观察',
      sour: '酸性',
    },
    firstFlipTpl: '首翻转 @ 第 {month} 月: {from} → {to}',
    stableLabel: '稳定',
  },
  continuousAudit: {
    controlLabel: '夜间全场审计',
    controlHint:
      '对所有资产、合同、规则的一次扫描。Ontology 重评估并列出每一处漂移或隐性失效的约束。',
    runButton: '▶ 运行审计',
    runningLabel: '扫描中…',
    resetButton: '重置',
    progressTpl: '已扫 {scanned} / {total} 台',
    summaryTitle: '发现项',
    summaryCountTpl: '{count} 条发现',
    bySeverityTpl: '{critical} 严重 · {warn} 观察 · {info} 信息',
    severityLabels: {
      critical: '严重',
      warn: '观察',
      info: '信息',
    },
    draftFixButton: '草拟修复 →',
    emptyStateTitle: '尚未运行',
    emptyStateBody: '点 run。发现项随审计逐个流入。',
    insightTitle: '聚合,不是告警',
    insightBody:
      '区别于事件驱动响应:这里是连续运行 + 单一看板汇总,不是一堆独立告警。每一条发现都自带"草拟修复"按钮。',
    findings: [
      {
        id: 'f1',
        asset: 'P-1023A',
        rule: 'R-CERT-CURRENT-02',
        severity: 'critical',
        title: 'NACE 证书过版',
        detail: '资产引用 NACE 2024-08,现行版 2025-03。酸性服务偏差触发。',
        fixDraft: '发起证书续审申请 + 在途 RFQ 标记为受阻',
      },
      {
        id: 'f2',
        asset: 'V-1040',
        rule: 'R-DRIFT-H2S-04',
        severity: 'critical',
        title: 'H₂S 读数 30 天 +12%',
        detail: '持续 12% 上涨穿过 watch 区间 — 接近酸性分类。',
        fixDraft: '安排 MOC 审查 + 紧急邀替代供应商进行材质升级',
      },
      {
        id: 'f3',
        asset: '供应商 B',
        rule: 'R-AVL-AUDIT-09',
        severity: 'warn',
        title: 'AVL 复审窗口已过',
        detail: '油田 AVL 复审超期 14 天。状态 SUSPENDED 等待复审。',
        fixDraft: '自动安排现场审计 + 冻结新 RFQ',
      },
      {
        id: 'f4',
        asset: 'RFQ 29133',
        rule: 'R-RFQ-STALE-07',
        severity: 'warn',
        title: 'RFQ 引用旧 NACE 阈值',
        detail: 'RFQ-29133 规格引用 2025-03 之前的 NACE 阈值。投标可能按旧规评估。',
        fixDraft: '通知采购按现行规范重发 RFQ',
      },
      {
        id: 'f5',
        asset: 'P-1031',
        rule: 'R-VIB-TREND-12',
        severity: 'warn',
        title: '振动趋势超带宽',
        detail: '90 天振动斜率 0.04 mm/s/d — 超 0.025 mm/s/d 阈值已 28 天。',
        fixDraft: '安排预测性维护检查 · 挂入下一次计划停机',
      },
      {
        id: 'f6',
        asset: '集团 MOC 日志',
        rule: 'R-MOC-CLOSURE-15',
        severity: 'info',
        title: 'MOC 项目超 SLA',
        detail: 'MOC-2025-074 已开 23 天;SLA 14 天。',
        fixDraft: '升级到 MOC 主席 + 利益相关方确认后自动延期',
      },
      {
        id: 'f7',
        asset: '合同 C-118',
        rule: 'R-CONTRACT-RENEWAL-03',
        severity: 'info',
        title: '合同将在 45 天后自动续约',
        detail: '年额 $840 万,除非在 2025-07-30 前取消否则自动续约。',
        fixDraft: '路由到采购作续约复核 · 附最新风险评分',
      },
      {
        id: 'f8',
        asset: 'V-1020',
        rule: 'R-CONS-RECON-02',
        severity: 'info',
        title: '跨系统读数不一致',
        detail: 'PI 读 90 ppm;实验室样本 105 ppm。偏差 > 10%。',
        fixDraft: '安排对账 + 标记传感器校准窗口',
      },
    ],
  },
  multiAgent: {
    incidentLabel: '新进事件',
    incidentTitle: 'INCIDENT-2025-K3041 · P-1023A 一级泄漏',
    incidentDetail: '02:14 当地发现 · CGS-3 A 列 · 酸性服务进料泵 · 无 HSE 暴露',
    injectButton: '▶ 注入工作区',
    injectedLabel: '工作区活动',
    resetButton: '重置',
    timelineLabel: 'Agent 时间线',
    workspaceLabel: '共享 ontology 工作区',
    emptyStateTitle: '尚未注入事件',
    emptyStateBody:
      '点 inject。每个 Agent 读最新图谱状态,贴自己的贡献,下一个 Agent 接着反应。不走点对点消息 — 只走共享 ontology。',
    insightTitle: '共享图谱,而非点对点',
    insightBody:
      '每个 Agent 读最新图谱、贴贡献、下一个接力。新 Agent 加入只需声明消费/产出的节点类型,不用谈判任何协议。',
    agents: [
      { id: 'triage', icon: '🔍', name: '分诊 Agent', accent: 'amber' },
      { id: 'diagnosis', icon: '🩺', name: '诊断 Agent', accent: 'cyan' },
      { id: 'fix', icon: '🔧', name: '修复 Agent', accent: 'violet' },
      { id: 'human', icon: '👤', name: '人工介入', accent: 'pass' },
    ],
    timeline: [
      {
        id: 'ma-1',
        timestamp: '02:14:07',
        agentId: 'triage',
        verb: '发布',
        nodeId: 'ma-asset',
        nodeKind: 'object',
        nodeLabel: 'Asset::P-1023A',
        nodeDetail: '酸性服务进料泵 · CGS-3 A 列 · 严重度=一级',
      },
      {
        id: 'ma-2',
        timestamp: '02:14:09',
        agentId: 'triage',
        verb: '发布',
        nodeId: 'ma-event',
        nodeKind: 'inference',
        nodeLabel: 'Event::一级泄漏',
        nodeDetail: 'PI 标签 CGS3.P1023A.LEAK = 1 · 已控 · 无 HSE 暴露',
        parents: ['ma-asset'],
      },
      {
        id: 'ma-3',
        timestamp: '02:14:11',
        agentId: 'diagnosis',
        verb: '假设',
        nodeId: 'ma-h1',
        nodeKind: 'inference',
        nodeLabel: 'Hypothesis::腐蚀',
        nodeDetail: '概率 0.72 · 30 天内 H₂S 12.6% → 18.2%',
        parents: ['ma-event'],
      },
      {
        id: 'ma-4',
        timestamp: '02:14:12',
        agentId: 'diagnosis',
        verb: '假设',
        nodeId: 'ma-h2',
        nodeKind: 'inference',
        nodeLabel: 'Hypothesis::机封磨损',
        nodeDetail: '概率 0.18 · 上次大修 2024-09 · 仍在服役曲线内',
        parents: ['ma-event'],
      },
      {
        id: 'ma-5',
        timestamp: '02:14:14',
        agentId: 'fix',
        verb: '发布',
        nodeId: 'ma-c-moc',
        nodeKind: 'constraint',
        nodeLabel: 'R-MOC-INCIDENT-02',
        nodeDetail: '酸性服务一级泄漏 → 48 小时内必走 MOC',
        parents: ['ma-event'],
      },
      {
        id: 'ma-6',
        timestamp: '02:14:16',
        agentId: 'fix',
        verb: '提议',
        nodeId: 'ma-plan',
        nodeKind: 'candidate',
        nodeLabel: 'Plan::加急 RFQ',
        nodeDetail: '邀 D 供应商 · 14 天 ETA · 并行强化巡检',
        parents: ['ma-h1', 'ma-c-moc'],
      },
      {
        id: 'ma-7',
        timestamp: '02:14:19',
        agentId: 'human',
        verb: '批准',
        nodeId: 'ma-act',
        nodeKind: 'action',
        nodeLabel: 'Action::加急 RFQ + 启 MOC',
        nodeDetail: '生产经理签字 · MOC 主席已告 · 日报开启',
        parents: ['ma-plan'],
      },
    ],
  },
}
